import OfferPresentationService, {
    PageComponentsMap,
    SportsLayoutsPage,
} from '@core/services/offerPresentation/OfferPresentationService';
import { useContextChange } from '@src/app/shared/featureFlags/useContextChange';
import { getConfig } from '@superbet-group/web.lib.config';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import { ref } from '@vue/composition-api';
import { HomeComponent } from './homeComponentsList';
import { SportsComponent } from './sportsComponentsList';

const pagesMappedToComponentKeys: PageComponentsMap = {
    [SportsLayoutsPage.HOME]: Object.values(HomeComponent),
    [SportsLayoutsPage.SPORTS]: Object.values(SportsComponent),
};

export function useSportsLayouts(page: SportsLayoutsPage) {
    const isLoading = ref(true);
    const areSportsLayoutsEnabled = ref(false);
    const enabledComponents = ref<PageComponentsMap[SportsLayoutsPage]>([]);

    async function getAreSportsLayoutsEnabled() {
        try {
            areSportsLayoutsEnabled.value = await featureFlagService.getFeatureValue('offer-presentation-sports-layouts', false);
        } catch (err) {
            areSportsLayoutsEnabled.value = false;
        }
    }

    async function getLayout() {
        try {
            const layouts = await OfferPresentationService.getSportsLayouts(page, getConfig().countrySpecific.country);

            // We want to use the first layout that is enabled
            const results = await Promise.all(
                layouts.map(async (layout) => {
                    const isEnabled = await featureFlagService.getFeatureValue(layout.featureFlagKey, false);
                    return isEnabled ? layout : null;
                }),
            );
            const layout = results.find((layout) => layout !== null);

            if (layout) {
                enabledComponents.value = layout.components;
                return;
            }

            // Make sure we always have a layout to show
            const defaultLayout = layouts.find((layout) => layout.isDefault) ?? layouts[0];

            enabledComponents.value = defaultLayout ? defaultLayout.components : pagesMappedToComponentKeys[page];
        } catch (err) {
            // Fallback to the local full list of components in case of error
            enabledComponents.value = pagesMappedToComponentKeys[page];
        }
    }

    useContextChange(async () => {
        await getAreSportsLayoutsEnabled();

        if (areSportsLayoutsEnabled.value) {
            await getLayout();
        }

        isLoading.value = false;
    });

    return {
        isLoading,
        areSportsLayoutsEnabled,
        enabledComponents,
    };
}
